import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { setCheckoutOverlay } from '../../../actions/ui';
import { setPresaleCode, setShowError, setShowWarning,
    validateCoupon, validatePresaleCoupon, submitCoupon, setCouponData, setIsPresaleValid,
    setIsPresaleApplied, setAutoCouponAmount, setShowAccessCodeError, setCouponInput } from '../actions/coupon';
import Coupon from '../components/Coupon';
import PresaleCoupon from '../components/PresaleCoupon';
import ContainerProps from './CouponContainerComponent';
import { reservationActive } from 'checkout/selectors/reservation';
import { deleteReservation } from '../../../actions/reservation';
import { isInOrgListing, isKioskMode } from '../../../selectors/ui';
import { initKeyboard } from 'checkout/actions/keyboard';
import { showPresaleCouponForm } from '../selectors/coupon';
import { getMainEvent } from '../../../selectors/event';
import { withNavigation } from '@checkout-b/helpers/withNavigation';
import { TICKETS_DISPLAY_LIST } from '@checkout-b/checkout/reducers/ui';

class CouponContainer extends Component<ContainerProps> {
    render() {
        if (this.props.showPresaleCouponForm) {
            return(
                <PresaleCoupon
                    presaleCode={this.props.presaleCode}
                    showError={this.props.showError}
                    showSuccess={this.props.showSuccess}
                    showWarning={this.props.showWarning}
                    isPresaleValid={this.props.isPresaleValid}
                    onPresaleCodeChange={this.onPresaleCodeChange}
                    onPresaleCodeCancel={this.onPresaleCodeCancel}
                    onPresaleCodeSubmit={this.onPresaleCodeSubmit}
                    isKioskMode={this.props.isKioskMode}
                    asOverlay={this.props.asOverlay}
                    canCancel={this.props.canCancelPresale}
                    disabled={this.props.taggableCouponLoading}
                    loading={this.props.loading}
                    presaleWording={this.props.presaleWording}
                />
            );
        } else {
            return(
                <Coupon
                    autoCouponAmount={this.props.autoCouponAmount}
                    onCodeChange={this.onCodeChange}
                    onCancelCoupon={this.onCancel}
                    onCodeSubmit={this.onCodeSubmit}
                    showError={this.props.showError}
                    showSuccess={this.props.showSuccess}
                    showWarning={this.props.showWarning}
                    showAccessCodeError={this.props.showAccessCodeError}
                    showAccessCodeSuccess={this.props.showAccessCodeSuccess}
                    isKioskMode={this.props.isKioskMode}
                    onFocus={this.onCouponFocus}
                    code={this.props.couponInput}
                    asOverlay={this.props.asOverlay}
                    disabled={this.props.taggableCouponLoading}
                    loading={this.props.loading}
                    title={this.props.customPromoCodeRequestText}
                />
            );
        }
    }

    onCouponFocus = (e, label) => {
        this.props.initKeyboard(e.target, label, setCouponInput);
    }

    onPresaleCodeChange = (e) => {
        this.props.setPresaleCode(e.target.value);
        this.props.setShowError(false);
        this.props.setShowWarning(false);
    };

    onPresaleCodeCancel = () => {
        this.props.setCheckoutOverlay(false);
        this.props.setPresaleCode('');
        this.props.setShowError(false);
        if(!this.props.isInOrgListing && this.props.reservationActive) {
            this.props.deleteReservation();
        }
    };

    onPresaleCodeSubmit = () => {
        this.props.validatePresaleCoupon().then(() => {});
    };

    onCodeChange = (code) => {
        this.props.setCouponInput(code);
        this.props.setShowError(false);
        this.props.setShowWarning(false);
    };

    ticketPickerOrListing = () => {
        if (this.props?.ticketsDisplayType === TICKETS_DISPLAY_LIST) {
            this.props.setCheckoutOverlay(false);
        } else {
            this.props.navigate("/");
        }
    }

    onCancel = () => {
        //in kiosk mode, take the user back to the state they came from
        if (this.props.isKioskMode) {
            this.kioskCallbackState();
        } else {
            if (this.props.tickets.length > 0) {
                this.props.navigate(`/cart/${this.props.token}`);
            } else {
                this.ticketPickerOrListing();
            }
        }

        if (!this.props.code) {
            this.props.setShowWarning(false);
        }
        this.props.setShowError(false);
        this.props.setShowAccessCodeError(false);
    };

    onCodeSubmit = codeSubmitted => {
        if(!this.props.reservationActive) {
            this.props.validateCoupon({ code: codeSubmitted }).then(() => {
                if (this.props.showError === false) {
                    this.ticketPickerOrListing();
                }
            });
        } else {
            let couponAlreadySubmitted = this.props.couponData.filter(couponEntry => codeSubmitted === couponEntry.code);
            if (this.props.couponData && couponAlreadySubmitted.length && !this.props.isInOrgListing) {
                this.props.setShowWarning(true);
                return false;
            }

            this.props.validateCoupon({ code: codeSubmitted }).then(() => {
                if (this.props.showSuccess === true) {
                    const codeAlreadyOnReservation = this.props.isInOrgListing &&
                        this.props.couponData.some(entry => entry.code === codeSubmitted);

                    if ((this.props.tickets.length > 0 || this.props.bundleSales.length) && !codeAlreadyOnReservation) {
                        //Only submit this coupon if it is a discount coupon.
                        //If it is an access coupon, it was already submitted in
                        //the validateCoupon action
                        if (this.props.accessCode !== codeSubmitted) {
                            this.props.submitCoupon().then(() => {
                                if (this.props.showSuccess === true) {
                                    if (this.props.isKioskMode) {
                                        this.kioskCallbackState();
                                    } else {
                                        this.props.navigate(`/cart/${this.props.token}`);
                                    }
                                }
                            });
                        }
                    } else {
                        if (this.props.isKioskMode) {
                            this.kioskCallbackState();
                        } else {
                            this.props.navigate("/");
                        }
                    }
                }
            });
        }
    };

    //in kiosk mode, coupon entry button can be in multiple places.
    //return to the place where the user clicked on the button
    kioskCallbackState = () => {
        const hasTickets = Array.isArray(this.props.tickets) && this.props.tickets.length;
        const hasBundles = Array.isArray(this.props.bundleSales) && this.props.bundleSales.length;
        if ((hasTickets || hasBundles) && this.props.reservationActive) {
            this.props.navigate(`/checkout/${this.props.token}`);
        } else {
            this.props.navigate("/");
        }
    };
}

const mapStateToProps = (state) => {
    return {
        code: state.coupon.code,
        couponInput: state.coupon.couponInput,
        showError: state.coupon.showError,
        showAccessCodeError: state.coupon.showAccessCodeError,
        showAccessCodeSuccess: state.coupon.showAccessCodeSuccess,
        showSuccess: state.coupon.showSuccess,
        showWarning: state.coupon.showWarning,
        token: state.reservation.token,
        tickets: state.tickets.tickets,
        reservationActive: reservationActive(state),
        couponData: state.coupon.couponData,
        presaleCode: state.coupon.presaleCode,
        isPresaleValid: state.coupon.isPresaleValid,
        priceLevels: state.priceLevel,
        ticketPicker: state.ticketPicker,
        autoCouponAmount: state.coupon.autoCouponAmount,
        listing: state.event.listing,
        isKioskMode: isKioskMode(state),
        bundleSales: state.bundleSales.bundles,
        accessCode: state.coupon.accessCode,
        showPresaleCouponForm: showPresaleCouponForm(state),
        taggableCouponLoading: state.couponTag.taggableCouponLoading,
        loading: state.coupon.loading,
        ticketsDisplayType: state.ui.listingConfig?.listings?.ticketsDisplayType,
        isInOrgListing: isInOrgListing(state),
        presaleWording: state.ui.listingConfig?.presale_wording,
        customPromoCodeRequestText: state.ui.listingConfig?.custom_promo_code_request_text,
    }
};

const mapDispatchToProps = dispatch => {
    return bindActionCreators({
        setCheckoutOverlay,
        setPresaleCode,
        setCouponData,
        setShowError,
        setShowWarning,
        setIsPresaleValid,
        validateCoupon,
        validatePresaleCoupon,
        submitCoupon,
        setIsPresaleApplied,
        setAutoCouponAmount,
        deleteReservation,
        setShowAccessCodeError,
        setCouponInput,
        initKeyboard,
    }, dispatch);
};

CouponContainer.propTypes = {
    code: PropTypes.string,
    presaleCode: PropTypes.string,
    showError: PropTypes.bool,
    showSuccess: PropTypes.bool,
    showWarning: PropTypes.bool,
    isPresaleValid: PropTypes.bool,
    showPresaleCouponForm: PropTypes.bool,
    couponData: PropTypes.array,
    autoCouponAmount: PropTypes.string,
    asOverlay: PropTypes.bool,
    canCancelPresale: PropTypes.bool,
    navigate: PropTypes.func,
    token: PropTypes.string,
    presaleWording: PropTypes.string,
    customPromoCodeRequestText: PropTypes.string,
};

CouponContainer.defaultProps = {
    asOverlay: true,
    canCancelPresale: true,
};

export default withNavigation(connect(mapStateToProps, mapDispatchToProps)(CouponContainer));
